import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useCommandPaletteStore from '../../stores/commandPaletteStore';
import useModuleStore from '../../stores/moduleStore';
import { CHORD_MAP } from './commands';

// Erkennt, ob der Fokus in einem editierbaren Feld liegt.
// Wenn ja: "g→letter"-Chords werden unterdrueckt, Cmd+K bleibt aktiv.
function isEditableTarget(el) {
  if (!el) return false;
  const tag = (el.tagName || '').toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  // Einige Editoren markieren ihre Roots nur ueber data-attrs — defensiv pruefen.
  if (el.getAttribute && el.getAttribute('role') === 'textbox') return true;
  return false;
}

/**
 * Globale Keyboard-Shortcuts:
 *  - Cmd+K / Ctrl+K  → Palette toggle (ueberall aktiv, preventDefault um
 *                      Browser-Suche zu unterdruecken)
 *  - g → letter      → Schnellnavigation (nur wenn Fokus NICHT in Editor)
 *
 * Der Hook installiert genau einen window-Listener und raeumt ihn auf.
 */
export default function useGlobalShortcuts() {
  const navigate = useNavigate();
  const { isEnabled } = useModuleStore();
  const {
    togglePalette,
    closePalette,
    leaderActive,
    startLeader,
    stopLeader,
  } = useCommandPaletteStore();

  useEffect(() => {
    function handler(e) {
      // ── Cmd+K / Ctrl+K → Palette ────────────────
      const isCmdK = (e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey);
      if (isCmdK) {
        e.preventDefault();
        e.stopPropagation();
        togglePalette();
        return;
      }

      // ── Escape schliesst Palette (Backup, UI macht es auch) ──
      if (e.key === 'Escape') {
        // Nicht hart closen, damit Modals/Dialoge ihr Escape behalten.
        // Palette selbst handhabt Escape im Input.
        return;
      }

      // ── "g→letter" Leader-Chord ─────────────────
      // Nur wenn Fokus nicht in einem Input/Textarea/ContentEditable liegt.
      if (isEditableTarget(document.activeElement)) return;

      // Modifier-Keys ignorieren (g+shift etc. sollen nichts ausloesen)
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // Leader starten: "g"
      if (!leaderActive && (e.key === 'g' || e.key === 'G')) {
        e.preventDefault();
        startLeader();
        return;
      }

      // Leader aktiv: naechster Buchstabe entscheidet
      if (leaderActive) {
        const letter = (e.key || '').toLowerCase();
        stopLeader();

        const target = CHORD_MAP[letter];
        if (!target) return;

        // Modul-Check — wenn deaktiviert, nichts tun (stille Eskalation).
        if (target.module && !isEnabled(target.module)) return;

        e.preventDefault();
        closePalette(); // falls offen, zu
        navigate(target.path);
      }
    }

    window.addEventListener('keydown', handler, true); // capture → laeuft vor Components
    return () => window.removeEventListener('keydown', handler, true);
  }, [togglePalette, closePalette, leaderActive, startLeader, stopLeader, navigate, isEnabled]);
}
